import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import { Button } from '@devhop/ui';
import { HelpCircle } from 'lucide-react';
import { usePermissions } from '@/modules/auth/presentation/model/usePermissions';
import { Permissions } from '@/modules/roles/domain/contracts/permissions';
import type { CompanyPolicyListItem } from '@/modules/hr/company-policy/domain/types';
import { useCompanyPoliciesQuery } from '../api/queries';
import {
    PolicyArchiveTour,
    usePolicyArchiveTour,
} from '../tour/PolicyArchiveTour';
import { PolicyFilter, type PolicySearchParams } from '../ui/PolicyFilter';
import { PolicyTable } from '../ui/PolicyTable';

export type PolicyArchiveSearchParams = Omit<PolicySearchParams, 'status'> & {
    offset?: number;
    limit?: number;
    sort?: Array<{ field: string; dir: 'asc' | 'desc' }>;
};

export function PolicyArchivePage() {
    const nav = useNavigate({ from: '/app/hr/company-policy/archive' });
    const search = useSearch({
        from: '/app/hr/company-policy/archive',
    }) as PolicyArchiveSearchParams;

    const sortBy = search.sort?.[0]?.field ?? 'updatedAt';
    const sortOrder = (search.sort?.[0]?.dir ?? 'desc') as 'asc' | 'desc';

    const query = useMemo(
        () => ({
            offset: search.offset ?? 0,
            limit: search.limit ?? 20,
            sort: search.sort,
            policyTypeId: search.policyTypeId,
            status: 'archived' as const,
            search: search.search,
        }),
        [
            search.offset,
            search.limit,
            search.sort,
            search.policyTypeId,
            search.search,
        ],
    );

    const listQuery = useCompanyPoliciesQuery(query);
    const data = listQuery.data?.data ?? [];
    const meta = listQuery.data?.meta;
    const totalCount = meta?.total ?? 0;
    const limit = meta?.limit ?? 20;
    const offset = search.offset ?? 0;

    const canCreate = usePermissions().has(
        Permissions.hr_company_policy.create,
    );
    const tour = usePolicyArchiveTour();

    const handlePaginationChange = (newOffset: number, newLimit: number) => {
        nav({
            search: {
                offset: newOffset,
                limit: newLimit,
                sort: search.sort,
                policyTypeId: search.policyTypeId,
                search: search.search,
            },
        });
    };

    const handleView = (row: CompanyPolicyListItem) => {
        nav({ to: '/app/hr/company-policy/$id', params: { id: row.id } });
    };

    const handleEdit = (row: CompanyPolicyListItem) => {
        nav({ to: '/app/hr/company-policy/$id/edit', params: { id: row.id } });
    };

    const handleDuplicate = useCallback(
        (row: CompanyPolicyListItem) => {
            nav({
                to: '/app/hr/company-policy/new',
                search: { duplicateFrom: row.id },
            });
        },
        [nav],
    );

    const handleSortingChange = useCallback(
        (id: string, desc: boolean) => {
            nav({
                search: {
                    offset: 0,
                    limit: search.limit ?? 20,
                    sort: [
                        {
                            field: id,
                            dir: (desc ? 'desc' : 'asc') as 'asc' | 'desc',
                        },
                    ],
                    policyTypeId: search.policyTypeId,
                    search: search.search,
                },
            });
        },
        [nav, search.limit, search.policyTypeId, search.search],
    );

    return (
        <>
            <Header />
            <Main>
                <PolicyArchiveTour
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-center justify-between"
                    data-tourid="policy-archive-toolbar"
                >
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-semibold text-2xl">
                                ນະໂຍບາຍເກັບໄວ້
                            </h1>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="size-7 rounded-full"
                                onClick={tour.startTour}
                                title="ເລີ່ມທົດສອບການນຳທາງ"
                                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                            >
                                <HelpCircle className="size-5" />
                            </Button>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            ເບິ່ງນະໂຍບາຍທີ່ເກັບໄວ້ ຄົ້ນຫາ ແລະ ກັ່ນຕອງຕາມປະເພດ
                        </p>
                    </div>
                </div>

                <div
                    className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
                    data-tourid="policy-archive-filter"
                >
                    <PolicyFilter
                        searchParams={{ ...search, limit: search.limit ?? 20 }}
                        fromRoute="/app/hr/company-policy/archive"
                        hideStatusFilter
                    />
                    <div data-tourid="policy-archive-table">
                        <PolicyTable
                        data={data}
                        isLoading={listQuery.isLoading}
                        offset={offset}
                        limit={limit}
                        totalCount={totalCount}
                        onPaginationChange={handlePaginationChange}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSortingChange={handleSortingChange}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDuplicate={canCreate ? handleDuplicate : undefined}
                    />
                    </div>
                </div>
            </Main>
        </>
    );
}
